import React, { useEffect, useRef, useState } from 'react';
import { CreditCard, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const ProfileDropdown = ({ isOpen, onClose, anchorRef }) => {
  const dropdownRef = useRef(null);
  const { userProfile, isSubscribed, signOut } = useAuth();
  const [billingLoading, setBillingLoading] = useState(false);

  const isElectron = !!window.electronAPI?.isElectron;

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const email = userProfile?.email || window.Clerk?.user?.primaryEmailAddress?.emailAddress || '';

  const handleManageSubscription = async () => {
    setBillingLoading(true);
    try {
      const token = await window.Clerk.session?.getToken();
      const apiUrl = window.QUER_CONFIG?.apiURL || (window.CONFIG?.baseUrl + window.CONFIG?.apiEndpoints?.go);
      const res = await fetch(`${apiUrl}/billing-portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (res.status === 404 || res.status === 400) {
        alert('No active subscription to manage.');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { url } = await res.json();

      if (isElectron && window.electronAPI?.openExternalLink) {
        window.electronAPI.openExternalLink(url);
      } else {
        window.location.href = url;
      }
      onClose();
    } catch (error) {
      console.error('[ProfileDropdown] Error opening billing portal:', error);
    } finally {
      setBillingLoading(false);
    }
  };

  const handleAccountSettings = () => {
    window.location.href = '/account';
    onClose();
  };

  const handleSignOut = async () => {
    onClose();
    await signOut();
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-60 bg-slate-900 border border-white/10 rounded-lg shadow-lg z-[60] overflow-hidden"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10">
        <p className="text-sm text-slate-200 truncate">{email}</p>
        <span className={`mt-1 inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
          isSubscribed
            ? 'bg-emerald-900/30 text-emerald-400'
            : 'bg-slate-700 text-slate-400'
        }`}>
          {isSubscribed ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Menu items */}
      <div className="py-1">
        <button
          onClick={handleManageSubscription}
          disabled={billingLoading}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-slate-200 transition-colors disabled:opacity-50"
        >
          <CreditCard size={16} className="text-slate-400 shrink-0" />
          {billingLoading ? 'Opening...' : 'Manage Subscription'}
        </button>

        {!isElectron && (
          <button
            onClick={handleAccountSettings}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-slate-200 transition-colors"
          >
            <Settings size={16} className="text-slate-400 shrink-0" />
            Account Settings
          </button>
        )}
      </div>

      {/* Divider + Sign Out */}
      <div className="border-t border-white/10 py-1">
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-slate-200 transition-colors"
        >
          <LogOut size={16} className="text-slate-400 shrink-0" />
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default ProfileDropdown;
